import React from 'react';
import { Play, Gem, Loader2, ChevronLeft, Coins, Wrench } from 'lucide-react';
import { MAP_LABEL, MAP_THUMB, FEATURE_LABEL, STAGE_ICON, gbFmt } from './gbApi';
import { GbTerrainPreview } from './GbTerrainPreview';
import { bossTaunt } from './gbTaunts';

export const Stars = ({ n, size = 'text-[11px]' }) => (
  <span className={`${size} leading-none tracking-tighter`}>
    <span className="text-amber-300">{'★'.repeat(n)}</span>
    <span className="text-purple-800">{'☆'.repeat(Math.max(0, 3 - n))}</span>
  </span>
);

export const gbCost = (cost, stars) =>
  Math.round(Number(cost || 0) * (3 - Math.max(0, Math.min(3, Number(stars || 0)))) / 3);

export const GbNodeSheet = ({ node, stars, paid, locked, diamonds, starting, onPlay, onClose,
  travado, avisoTrava }) => {
  if (!node) return null;
  const total = Number(node.reward_total || 0);
  const got = Number(paid || 0);
  const falta = Math.max(0, total - got);
  const custo = gbCost(node.cost, stars);
  const zerado = stars >= 3;
  return (
    <div className="pwa-safe-top fixed inset-0 z-50 flex items-end justify-center bg-black/80 p-4 backdrop-blur-sm md:items-center"
      onClick={onClose} data-testid="gb-node-sheet">
      <div className="relative max-h-[94dvh] w-full max-w-md overflow-y-auto overflow-x-hidden rounded-3xl border border-purple-800/70 bg-[#150d26] shadow-[0_18px_60px_rgba(0,0,0,.85)]"
        onClick={(e) => e.stopPropagation()}>
        <div className="relative">
        <div className="sticky top-0 z-10 overflow-hidden bg-[#1a1030]">
          <img src={MAP_THUMB[node.map]} alt={node.map} className="absolute inset-0 h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(21,13,38,.75)_0%,rgba(21,13,38,.96)_100%)]" />
          <div className="relative flex items-center gap-3 p-3">
            <img src={STAGE_ICON(node.node)} alt="" data-testid="gb-sheet-stage-icon"
              className="h-12 w-12 shrink-0 object-contain drop-shadow-[0_4px_10px_rgba(0,0,0,.7)]" />
            <div className="min-w-0 flex-1">
              <h3 className="truncate text-lg font-black text-orange-300">{node.name || `Fase ${node.node}`}</h3>
              <p className="truncate text-[11px] text-purple-200/80">{MAP_LABEL[node.map] || node.map} · Nível {node.level}</p>
            </div>
            {node.opponent?.avatar && (
              <img src={node.opponent.avatar} alt={node.opponent.nickname}
                data-testid="gb-sheet-opponent-avatar"
                className="h-11 w-11 shrink-0 rounded-full border-2 border-rose-400/80 object-cover shadow-[0_0_16px_rgba(255,84,112,.45)]" />
            )}
          </div>
          <div className="relative flex gap-2 px-3 pb-3">
            <button onClick={onClose} data-testid="gb-node-cancel"
              className="flex items-center justify-center gap-1 rounded-2xl border border-purple-700/70 bg-black/40 px-4 py-2.5 text-xs font-bold text-purple-200 transition-colors hover:bg-black/60">
              <ChevronLeft className="h-4 w-4" />Voltar
            </button>
            <button
              data-testid="gb-node-play"
              disabled={locked || travado || diamonds < custo || !!starting}
              onClick={() => onPlay(node)}
              className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-orange-500 to-amber-400 py-2.5 text-xs font-black text-[#1b0d02] shadow-[0_0_24px_rgba(255,107,0,.5)] transition-transform active:scale-[.98] disabled:opacity-40"
            >
              {starting ? <Loader2 className="h-4 w-4 animate-spin" />
                : travado ? <Wrench className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              {travado ? 'EM MANUTENÇÃO' : diamonds < custo ? 'DIAMANTES INSUFICIENTES' : 'INICIAR FASE'}
            </button>
          </div>
        </div>
        {travado && (
          <div className="border-b border-amber-700/60 bg-amber-950/50 px-4 py-2.5"
            data-testid="gb-node-maintenance">
            <p className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-amber-400">
              <Wrench className="h-3.5 w-3.5" />manutenção programada
            </p>
            <p className="mt-1 text-[12px] font-bold leading-snug text-amber-100">
              {avisoTrava || 'Manutenção programada: as fases voltam em breve.'}
            </p>
            <p className="mt-1 text-[11px] text-amber-200/75">
              Nenhuma entrada é cobrada agora. Seu saldo e suas estrelas estão guardados.
            </p>
          </div>
        )}
        {node.opponent && (
          <div className="border-b border-purple-800/60 bg-[#100a1e] px-4 py-2" data-testid="gb-sheet-opponent">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-black uppercase text-purple-300/80">Oponente</span>
              <span className="text-sm font-bold text-rose-200">{node.opponent.nickname}</span>
              {node.opponent.title && <span className="text-[11px] text-purple-200/70">· {node.opponent.title}</span>}
            </div>
            <p className="relative mt-1.5 rounded-xl rounded-tl-none border border-rose-800/60 bg-[#2a0e18] px-3 py-1.5 text-[11px] italic text-rose-100"
              data-testid="gb-sheet-taunt">
              “{bossTaunt(node.opponent, node.node)}”
            </p>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2 p-4 text-xs">
          <div className="rounded-xl border border-purple-800/70 bg-[#100a1e] p-3">
            <p className="text-purple-200/80">Entrada {stars > 0 && !zerado && <span className="text-amber-300">(-{stars}★)</span>}</p>
            {custo > 0 ? (
              <p className="mt-1 flex items-center gap-1 font-mono text-base font-bold text-rose-300">
                <Gem className="h-4 w-4" />-{gbFmt(custo)}
                {stars > 0 && <s className="ml-1 text-[11px] text-purple-400/70">{gbFmt(node.cost)}</s>}
              </p>
            ) : (
              <p className="mt-1 flex items-center gap-1 font-mono text-base font-bold text-purple-200/70">
                <Gem className="h-4 w-4" />0
              </p>
            )}
          </div>
          <div className="rounded-xl border border-purple-800/70 bg-[#100a1e] p-3">
            <p className="text-purple-200/80">Prêmio da fase (3★)</p>
            <p className="mt-1 flex items-center gap-1 font-mono text-base font-bold text-amber-300">
              <Coins className="h-4 w-4" />{gbFmt(total)}
            </p>
            <p className="text-[10px] text-amber-200/70">moedas amarelas · o dobro da entrada</p>
          </div>
          <div className="col-span-2 rounded-xl border border-amber-700/60 bg-[#231506] p-3" data-testid="gb-sheet-stars">
            <div className="flex items-center justify-between">
              <p className="text-amber-200/90">Suas estrelas nesta fase</p>
              <Stars n={stars} size="text-base" />
            </div>
            <div className="mt-2 grid grid-cols-3 gap-1 text-center font-mono text-[11px]">
              {[1, 2, 3].map((k) => (
                <div key={k} className={`rounded-lg border px-1 py-1 ${stars >= k
                  ? 'border-amber-500/60 bg-amber-900/30 text-amber-200'
                  : 'border-purple-800/60 text-purple-200/70'}`}>
                  {'★'.repeat(k)} · {gbFmt(Math.floor((total * k) / 3))}
                </div>
              ))}
            </div>
            <p className="mt-2 text-[11px] text-purple-100/90">
              Já recebeu <b className="text-amber-300">{gbFmt(got)} moedas</b> desta fase.
              {falta > 0
                ? <> Ainda pode ganhar <b className="text-amber-300">{gbFmt(falta)} moedas</b> subindo as estrelas.
                  {stars > 0 && <> A entrada também cai a cada estrela conquistada.</>}</>
                : <> <b className="text-emerald-300">Fase dominada</b> — sem novo prêmio.</>}
            </p>
          </div>
          <div className="col-span-2">
            <GbTerrainPreview seed={node.terrain_seed} features={node.features} map={node.map} node={node.node} />
          </div>
          <div className="col-span-2 flex flex-wrap gap-2 text-[11px] text-purple-100/90">
            <span className="rounded-full border border-purple-800/70 bg-[#100a1e] px-2 py-1">🎯 Oponente {node.bot_hp} HP</span>
            <span className="rounded-full border border-purple-800/70 bg-[#100a1e] px-2 py-1">💥 Precisão {Math.round(node.bot_accuracy * 100)}%</span>
            <span className="rounded-full border border-purple-800/70 bg-[#100a1e] px-2 py-1">💨 Vento até {node.wind_max}</span>
            {(node.features || []).map((f) => (
              <span key={f} data-testid={`gb-sheet-feature-${f}`}
                className="rounded-full border border-amber-700/60 bg-[#231506] px-2 py-1 text-amber-200">
                {FEATURE_LABEL[f] || f}
              </span>
            ))}
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};
